import { isEmpty, isNil } from 'lodash';
import { useMemo } from 'react';
import { Options as RRuleOptions } from 'rrule';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { useResponsiveFormContext } from '@shared/scenes/entity/components/ResponsiveFormContext';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { API } from '@shared/types/api';
import { formatDateWithYear } from '@shared/util/date';
import { useIsDesktop } from '@shared/util/hooks';
import { i18n } from '@shared/util/i18n';
import { getRruleText } from './utils';

const unitLabels: Record<API.RecurrenceOffsetUnit, { singular: string; plural: string }> = {
  DAY: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.day.plural'),
  },
  WEEK: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.week.plural'),
  },
  MONTH: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.month.plural'),
  },
  YEAR: {
    singular: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.singular'),
    plural: i18n('workflowTemplate.component.dateAndRepeat.offset.unit.year.plural'),
  },
};

function getOffsetUnitLabel(unit: API.RecurrenceOffsetUnit, value: number): string {
  const isSingular = value === 1;
  return isSingular
    ? unitLabels[unit].singular.toLowerCase()
    : unitLabels[unit].plural.toLowerCase();
}

export function RepeatText({
  rruleOptions,
  includeStartDate,
  offset,
  tagFilter,
  workflowTemplateId,
}: {
  rruleOptions?: Partial<RRuleOptions>;
  includeStartDate?: boolean;
  offset?: API.CreateRecurrenceOffset | null;
  tagFilter?: API.FilterByComponentFragment;
  workflowTemplateId?: string;
}) {
  const responsiveForms = useResponsiveFormContext();
  const isDesktop = useIsDesktop();
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  // Validate offset configuration
  const hasValidTagFilter =
    !isNil(tagFilter?.componentId) && !isEmpty(tagFilter?.value?.containsString?.values);
  const hasValidOffset = !isNil(offset?.unit) && !isNil(offset?.value) && offset.value > 0;
  const isValidOneAtATime = hasValidTagFilter && hasValidOffset;

  // Get tag component label and selected tag value labels
  const { tagComponentLabel, tagValueLabels, tagValueCount } = useMemo(() => {
    if (!isValidOneAtATime || !workflowTemplate) {
      return { tagComponentLabel: '', tagValueLabels: '', tagValueCount: 0 };
    }

    const tagComponent = workflowTemplate.components.find(
      c => c.id === tagFilter?.componentId && isTagComponent(c),
    );

    if (!tagComponent || !isTagComponent(tagComponent)) {
      return { tagComponentLabel: '', tagValueLabels: '', tagValueCount: 0 };
    }

    const componentLabel = tagComponent.label ?? '';
    const selectedTagIds = tagFilter?.value?.containsString?.values ?? [];
    const selectedOptions =
      tagComponent.options?.filter(opt => selectedTagIds.includes(opt.value)) ?? [];
    const valueLabels = selectedOptions.map(opt => opt.label).join(', ');

    return {
      tagComponentLabel: componentLabel,
      tagValueLabels: valueLabels,
      tagValueCount: selectedOptions.length,
    };
  }, [isValidOneAtATime, workflowTemplate, tagFilter]);

  if (isValidOneAtATime && offset) {
    const offsetText = `${offset.value} ${getOffsetUnitLabel(offset.unit, offset.value)}`;

    return (
      <View
        row
        centerVertically
        paddingVertical={responsiveForms && isDesktop ? 1 : undefined}
        flex
      >
        <View style={{ flexGrow: 1, flexShrink: 1 }}>
          <Text size="body2" numberOfLines={5}>
            {i18n(
              tagValueCount > 1
                ? 'workflowTemplate.component.dateAndRepeat.offset.summaryMultiple'
                : 'workflowTemplate.component.dateAndRepeat.offset.summary',
              {
                fieldLabel: tagComponentLabel,
                tagValue: tagValueLabels,
                offset: offsetText,
              },
            )}
          </Text>
        </View>
      </View>
    );
  }

  const rruleText = getRruleText(rruleOptions);

  if (isNil(rruleOptions?.dtstart)) {
    return null;
  }

  const startMonth = rruleOptions.dtstart.getUTCMonth();
  const startDate = rruleOptions.dtstart.getUTCDate();
  const startYear = rruleOptions.dtstart.getUTCFullYear();
  const startDateString = `${startMonth + 1}/${startDate}/${startYear}`;

  return (
    <View row centerVertically flex>
      <View style={{ flexGrow: 1, flexShrink: 1 }}>
        <Text size="body2" numberOfLines={5}>
          {i18n('workflowTemplate.component.dateAndRepeat.repeats')} {rruleText}
          {includeStartDate && !isNil(rruleOptions.dtstart) && (
            <>
              {' '}
              {i18n('workflowTemplate.component.dateAndRepeat.startingOn', {
                date: formatDateWithYear(startDateString),
              })}
            </>
          )}
        </Text>
      </View>
    </View>
  );
}
